/**
 * Created by Daniel on 12/24/2019
 * -------------------------------------
 */

import { Component, OnInit, ViewChild, OnDestroy, AfterViewInit, Input } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Subscription, forkJoin, Observable, Subject } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';
import * as lodash from 'lodash';
import { Store, select } from '@ngrx/store';

import { SystemUser } from '../../../../tamalelibs/models/user.model';
import { StoreQuerierService } from '../../../../services/store-querier.service';
import { QuickTask, PriorityMode, OperationType, SubTask, QuickTaskStatus, Processor } from '../../../../tamalelibs/models/workflow.model';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../../widgets/notification/notification.model';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import { BriefConfig } from '../../../../widgets/widgets.model';
import { EntityService } from '../../../../tamalelibs/services/entity.service';
import { AppState } from '../../../../redux';
import { usersAndTeamsSelector } from '../../../../redux/reducers/users.reducer';
import { DateHelperService } from '../../../../tamalelibs/services/date-helper.service';
import { WorkflowTaskActionTypes } from '../../../../tamalelibs/redux/actions/workflow-task.actions';
import { lastCustomActionSelector } from '../../../../redux/reducers/action-history.reducer';
import { workflowSetTaskStatusSelector, workflowSetTaskProcessorSelector } from '../../../../tamalelibs/redux/reducers/workflow-task.reducer';
import { UsersService } from './../../../../tamalelibs/services/users.service';
import { SlideSheetActionTypes } from '../../../slide-sheet/slide-sheet.model';
import { SlideSheetService } from './../../../../services/slide-sheet.service';
declare let $: any;

export const NEW_QUICK_TASK_TITLE = 'New Quick Task';
export const DEFAULT_DATETIME_FORMAT = '9:00';
export const DEFAULT_EMPTY_FORMAT = '';

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'quick-task',
    templateUrl: './quick-task.component.html',
    styleUrls: ['./quick-task.component.scss']
})
export class QuickTaskComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() closeAction?: { subject: Subject<any>, action: any };

    @ViewChild('assigneelist', { static: false }) assigneelist;
    @ViewChild('datepicker', { static: false }) datepicker;
    @ViewChild('entitylist', { static: false }) entitylist;
    isProgressing = false;
    // form control
    taskFormGroup = new UntypedFormGroup({
        assignee: new UntypedFormControl([]),
        details: new UntypedFormControl(''),
        dueDate: new UntypedFormControl(''),
        finishFlag: new UntypedFormControl({ value: false, disabled: false }),
        finishNumber: new UntypedFormControl({ value: '', disabled: true }),
        priority: new UntypedFormControl('Normal'),
        remindDate: new UntypedFormControl(''),
        related: new UntypedFormControl([]),
        subject: new UntypedFormControl(''),
    });
    get assignee() {
        return this.taskFormGroup.get('assignee');
    }
    get details() {
        return this.taskFormGroup.get('details');
    }
    get dueDate() {
        return this.taskFormGroup.get('dueDate');
    }
    get finishFlag() {
        return this.taskFormGroup.get('finishFlag');
    }
    get finishNumber() {
        return this.taskFormGroup.get('finishNumber');
    }
    get priority() {
        return this.taskFormGroup.get('priority');
    }
    get related() {
        return this.taskFormGroup.get('related');
    }
    get remindDate() {
        return this.taskFormGroup.get('remindDate');
    }
    get subject() {
        return this.taskFormGroup.get('subject');
    }

    assigneeDataItems: Array<any> = [];
    briefConfig = new BriefConfig();
    controlReadonly = false;
    currentUser: SystemUser;
    dueDateData: Date;
    finishNumberSource = ['All'];
    hideFooter = false;
    isCurrentUserInAssigneeList = true;
    max: Date = new Date(2099, 12, 12);
    min: Date = new Date(1900, 1, 1);
    operationType = OperationType.Create;
    // priority properties
    prioritySource: Array<string> = ['Low', 'Normal', 'High'];
    // assign default vaule for priority
    quickTask: QuickTask;
    quickTaskTitle = NEW_QUICK_TASK_TITLE;
    // remind date properties
    remindDateDateData: Date;
    remindDateTimeData = '';
    remindDateTimeNotValid = true;
    reminderReadonly = false;
    relatedDataItems: Array<any> = [];
    showMarkAsDoneBtn = true;
    subTask: SubTask;

    private _basicFormData = {};
    private _destroySubscriptions: Array<Subscription> = [];
    private _firstLoad = false;
    private _isCreateQuickTaskFormDataChanged = false;

    constructor(
        private _alertWindow: AlertWindowService,
        private _dateHelperService: DateHelperService,
        private _entityService: EntityService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _toast: ToastService,
        private _userService: UsersService,
        private _slideSheetService: SlideSheetService,
    ) {
    }

    ngOnInit() {
        // init components
        this._initControls();
        // set controls status
        this._setControlsStatus();
        this._destroySubscriptions.push(
            this.taskFormGroup.valueChanges.subscribe(event => this._formInfoChange(event)),
            // filter values for assignee to
            this.briefConfig.actionSubject$.pipe(
                debounceTime(250),
                filter(res => res != null && res.payload.length > 0)
            ).subscribe(res => {
                if (res.type === 'assignee') {
                    this._searchingAssignee(res.payload);
                } else if (res.type === 'related') {
                    this._searchingRelated(res.payload);
                }
            }),
            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => this._actionMessageHandler(action)),
        );
        this._firstLoad = true;
    }

    ngAfterViewInit() {
        this._basicFormData = this.taskFormGroup.value;
        this._firstLoad = false;
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    assigneeFilter(value) {
        this.assigneelist.toggle(false);
        this.briefConfig.actionSubject$.next({
            type: 'assignee',
            payload: value
        });
    }

    disabledDueDates = (date: Date): boolean => {
        return date.getTime() < new Date().getTime() - 24 * 60 * 60 * 1000;
    };

    disabledRemindDates = (date: Date): boolean => {
        if (this.dueDate.value) {
            return date.getTime() < new Date().getTime() - 24 * 60 * 60 * 1000 ||
                date.getTime() > this.dueDate.value.getTime();
        } else {
            return date.getTime() < new Date().getTime() - 24 * 60 * 60 * 1000;
        }
    };

    onBtnCancel() {
        if (this._isCreateQuickTaskFormDataChanged) {
            const subscription = this._alertWindow.warn(
                'You have unsaved changes',
                ['Are you sure you want to discard the changes?'],
                'Discard',
                'Go Back',
                'slideSheet',
                true).subscribe((result: boolean) => {
                    if (result) {
                        if (this.closeAction) {
                            this.closeAction.subject.next(this.closeAction.action);
                        }
                    }
                    subscription.unsubscribe();
                });
        } else {
            if (this.closeAction) {
                this.closeAction.subject.next(this.closeAction.action);
            }
        }
    }

    onBtnClose() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CLOSE,
            payload: ''
        });
    }

    onBtnMarkAsDone() {
        if (this.subTask && this.subTask.subTaskId.length > 0) {
            this._markAsDoneAction(this.subTask.subTaskId);
        } else {
            const subTask = this.quickTask.subTask.find(item => item.assignee.id === this.currentUser.id);
            this._markAsDoneAction(subTask.subTaskId);
        }
    }

    onBtnSave() {
        if (this._doValidation()) {
            this.isProgressing = true;
            this._setControlsValues();
            if (this.operationType === OperationType.Create) {
                this._store.dispatch({
                    type: WorkflowTaskActionTypes.CREATE_QUICK_TASK,
                    payload: this.quickTask
                });
            } else if (this.operationType === OperationType.Edit) {
                const _processor = this._storeQuerier.queryBySelector(workflowSetTaskProcessorSelector);
                if (_processor === Processor.AssignedToMe) {
                    if (this.quickTask.originator.id === this.currentUser.id) {
                        this._store.dispatch({
                            type: WorkflowTaskActionTypes.EDIT_QUICK_TASK,
                            payload: {
                                quickTask: this.quickTask,
                                triggerBy: 'Save'
                            }
                        });
                    } else {
                        this._store.dispatch({
                            type: WorkflowTaskActionTypes.CHANGE_QUICK_TASK_STATUS,
                            payload: {
                                subTaskId: this.subTask.subTaskId,
                                remindDate: this._getReminderDate(),
                                type: 'RemindDateChange'
                            }
                        });
                    }
                } else {
                    this._store.dispatch({
                        type: WorkflowTaskActionTypes.EDIT_QUICK_TASK,
                        payload: {
                            quickTask: this.quickTask,
                            triggerBy: 'Save'
                        }
                    });
                }
            }
        }
    }

    onCheckedChange() {
        if (!this.finishFlag.disabled) {
            if (this.finishFlag.value) {
                this.finishNumber.enable();
            } else {
                this.finishNumber.disable();
                this.finishNumber.setValue('All');
            }
        }
    }

    onDueDateBlur() {
        const value = this.dueDate.value;
        if (this.remindDate.value) {
            if (!this.remindDateTimeData) {
                this.remindDateTimeData = DEFAULT_DATETIME_FORMAT;
                this.remindDateTimeNotValid = false;
            }
        } else {
            if (this.dueDateData) {
                this.remindDate.setValue(value);
                this.remindDateTimeData = DEFAULT_DATETIME_FORMAT;
                this.remindDateTimeNotValid = false;
            }
        }
    }

    onEnter(event) {
        event.stopImmediatePropagation();
    }

    onOpenAssignee(event: any): void {
        const inputValue = this.assigneelist.wrapper.nativeElement.querySelector('input', '.k-multiselect-wrap .k-floatwrap').value;
        if (!inputValue) {
            event.preventDefault();
        }
    }

    onOpenRelated(event: any): void {
        const inputValue = this.entitylist.wrapper.nativeElement.querySelector('input', '.k-multiselect-wrap .k-floatwrap').value;
        if (!inputValue) {
            event.preventDefault();
        }
    }

    onRemindDateDateBlur() {
        setTimeout(() => {
            if (this.remindDate && this.remindDate.value) {
                if (this.remindDate.value.getTime() > this.max.getTime() ||
                    this.remindDate.value.getTime() < this.min.getTime()) {
                    this.remindDateDateData = new Date();
                }
            }
        }, 0);
    }

    onRemindDateDateChange(value) {
        if (value) {
            this.remindDateTimeNotValid = false;
            if (this.remindDateTimeData === '') {
                this.remindDateTimeData = DEFAULT_DATETIME_FORMAT;
            }
        } else {
            this.remindDateTimeNotValid = true;
            this.remindDateTimeData = DEFAULT_EMPTY_FORMAT;
        }
    }

    onRemindDateTimeBlur(value) {
        if (!this._dateHelperService.judgeTimeFormat(value)) {
            this.remindDateTimeData = DEFAULT_DATETIME_FORMAT;
        }
    }

    onRemindDateTimeChange(value) {
        this.remindDateTimeData = value;
    }

    onRemindDateTimeClick(value) {
        this.remindDateTimeData = value;
    }

    relatedFilter(value) {
        this.entitylist.toggle(false);
        this.briefConfig.actionSubject$.next({
            type: 'related',
            payload: value
        });
    }

    valueChangeForAssignee(values) {
        if (values && values.length > 0) {
            if (!this.currentUser) {
                this.currentUser = this._storeQuerier.getCurrentUser();
            }
            if (this.operationType === OperationType.Edit) {
                // the 'markAsDone' button should be disabled if it's added in edi model
                this.showMarkAsDoneBtn = values.find(item => {
                    return item.id === this.currentUser.id &&
                        this.quickTask.assignee.every(aItem => aItem.id === this.currentUser.id);
                });
            }

            // get the all assignees
            const userIds = [];
            const teamIds = [];

            values.forEach(item => {
                if (item.type === 'team') {
                    teamIds.push(item.id);
                } else {
                    userIds.push(item.id);
                }
            });

            if (teamIds && teamIds.length > 0) {
                this._setFinishStatusForUserIdsAndTeamIds(teamIds, userIds);
            } else {
                this._setFinishStats(userIds);
            }
        } else {
            this.showMarkAsDoneBtn = false;
            this.finishFlag.setValue(false);
            this.finishFlag.disable();
        }
    }

    private _actionMessageHandler(action) {
        let msg = '';
        if (action.type === WorkflowTaskActionTypes.CREATE_QUICK_TASK_SUCCESS) {
            msg = 'Task ' + this.quickTask.subject + ' assigned successfully.';
            this._afterSavedSucessfully(msg);
        } else if (action.type === WorkflowTaskActionTypes.EDIT_QUICK_TASK_SUCCESS ||
            action.type === WorkflowTaskActionTypes.EDIT_QUICK_TASK_REMIND_DATE_SUCCESS) {
            if (action.payload === 'Save') {
                msg = 'Task ' + this.quickTask.subject + ' updated successfully.';
                this._afterSavedSucessfully(msg);
            } else if (action.payload === 'Done') {
                this._store.dispatch({
                    type: WorkflowTaskActionTypes.CHANGE_QUICK_TASK_STATUS,
                    payload: {
                        subTaskId: this.subTask.subTaskId,
                        type: 'TaskStatusChange'
                    }
                });
            }
        } else if (action.type === WorkflowTaskActionTypes.CHANGE_QUICK_TASK_STATUS_SUCCESS) {
            if (action.payload.status === 'done') {
                msg = 'Task ' + this.quickTask.subject + ' completed successfully.';
            } else {
                msg = 'Task ' + this.quickTask.subject + ' updated successfully.';
            }
            this._afterSavedSucessfully(msg);
        } else if (action.type === WorkflowTaskActionTypes.CHANGE_QUICK_TASK_STATUS_FAILED) {
            const errMsg = action.payload;
            if (errMsg) {
                const startIndex = errMsg.indexOf('INFO_TASK_HAS_BEEN_DONE:');
                if (startIndex > 0) {
                    const startEnd = errMsg.indexOf('(TransactionID:');
                    msg = errMsg.substring(startIndex + 24, startEnd);
                    this._toast.notify({
                        message: msg,
                        style: NotificationStyles.Error
                    });
                } else {
                    this._toast.notify({
                        message: errMsg,
                        style: NotificationStyles.Error
                    });
                }
            }
        }
    }

    private _afterSavedSucessfully(msg: string) {
        this.isProgressing = false;
        // popup notification after saved quick task sucessfully
        this._toast.notify({
            message: msg,
            style: NotificationStyles.Success
        });

        const status = this._storeQuerier.queryBySelector(workflowSetTaskStatusSelector);
        const processor = this._storeQuerier.queryBySelector(workflowSetTaskProcessorSelector);
        this._store.dispatch({
            type: WorkflowTaskActionTypes.GET_TASK_BY_STATUS,
            payload: {
                status: status,
                processor: processor
            }
        });
        if (this.closeAction) {
            this.closeAction.subject.next(this.closeAction.action);
        }
    }

    private _doValidation(): boolean {
        let dataValid = true;
        this.subject.setErrors(null);
        this.assignee.setErrors(null);

        if (!this.subject.value) {
            this.subject.setErrors({
                required: true
            });
            dataValid = false;
        } else if (this.subject.value.length > 128) {
            this.subject.setErrors({
                maxlength: true
            });
            dataValid = false;
        }

        if (this.details.value.length > 2048) {
            this.details.setErrors({
                maxlength: true
            });
            dataValid = false;
        }

        if (this.assignee.value.length === 0) {
            this.assignee.setErrors({
                required: true
            });
            dataValid = false;
        }

        return dataValid;
    }

    private _formInfoChange(event) {
        if (event && event.subject && event.subject.length > 128) {
            this.subject.setErrors({
                maxlength: true
            });
        }
        if (event && event.details && event.details.length > 2048) {
            this.details.setErrors({
                maxlength: true
            });
        }
        if (!this._firstLoad) {
            if (!lodash.isEqual(this._basicFormData, event)) {
                this._isCreateQuickTaskFormDataChanged = true;
            } else {
                this._isCreateQuickTaskFormDataChanged = false;
            }
        }
    }

    private _getArrayByUserCount(userCount: number) {
        let finishNumberArray = [];
        let numbarArray = [];

        if (userCount > 0) {
            while (userCount > 0) {
                numbarArray.push(userCount.toString());
                userCount--;
            }
        }
        numbarArray = numbarArray.sort(function (a, b) {
            return a.toLowerCase().localeCompare(b.toLowerCase());
        });
        finishNumberArray.push('All');
        finishNumberArray = finishNumberArray.concat(numbarArray);

        return finishNumberArray;
    }

    private _getShortDateByDateString(dateVal: string) {
        const container = dateVal.split('-');
        const year = container[0];
        const month = container[1];
        const day = container[2];
        const date = month + '/' + day + '/' + year;

        return new Date(date);
    }

    private _getPriorityValue(value) {
        switch (value) {
            case 'Low':
                return PriorityMode.Low;
            case 'Normal':
                return PriorityMode.Normal;
            case 'High':
                return PriorityMode.High;
        }
    }

    private _getReminderDate() {
        let reminderDate = '';

        if (this.remindDate.value) {
            const date = this._dateHelperService.getDateStringByDateObject(this.remindDate.value, 'yyyy-mm-dd');
            reminderDate = date;

            if (this.remindDateTimeData && this.remindDateTimeData.length > 0) {
                const timeContainer = this.remindDateTimeData.split(':');
                const hh = timeContainer[0].padStart(2, '0');
                const mm = timeContainer[1].padStart(2, '0');
                const time = hh + ':' + mm + ':00';
                reminderDate = date + ' ' + time;
            }
        }

        return reminderDate;
    }

    private _initControls() {
        const allUsersAndTeams = this._storeQuerier.queryBySelector(usersAndTeamsSelector);
        this.currentUser = this._storeQuerier.getCurrentUser();
        if (this.operationType === OperationType.Edit) {
            // assign saved value for title
            this.quickTaskTitle = this.quickTask.subject;
            // assign saved value for subject
            this.subject.setValue(this.quickTask.subject);
            // assign saved value for detail
            this.details.setValue(this.quickTask.detail);
            // assign saved value for priority
            if (this.quickTask.priority === PriorityMode.Low) {
                this.priority.setValue('Low');
            } else if (this.quickTask.priority === PriorityMode.Normal) {
                this.priority.setValue('Normal');
            } else if (this.quickTask.priority === PriorityMode.High) {
                this.priority.setValue('High');
            } else {
                this.priority.setValue('Normal');
            }
            // assing saved value for due date
            if (this.quickTask.dueDate) {
                const date = this._getShortDateByDateString(this.quickTask.dueDate);
                this.dueDate.setValue(date);
            }
            // assign saved value for remind date
            let remindDate = this.quickTask.remindDate.replace(/\-/g, '/');
            const _processor = this._storeQuerier.queryBySelector(workflowSetTaskProcessorSelector);
            if (_processor === Processor.AssignedToMe && this.subTask.remindDate) {
                remindDate = this.subTask.remindDate.replace(/\-/g, '/');
            }
            if (remindDate) {
                let date = new Date(remindDate);
                if (remindDate.indexOf(':') <= 0) {
                    date = new Date(remindDate + ' ' + '00:00');
                }

                this.remindDate.setValue(date);

                this.remindDateDateData = date;
                if (this.remindDateDateData) {
                    this.remindDateTimeNotValid = false;
                    if (remindDate.indexOf(':') > 0) {
                        const hh = date.getHours();
                        const mm = date.getMinutes().toString().padStart(2, '0');
                        this.remindDateTimeData = hh + ':' + mm;
                    } else {
                        this.remindDateTimeData = DEFAULT_EMPTY_FORMAT;
                    }
                } else {
                    this.remindDateTimeNotValid = true;
                    this.remindDateTimeData = DEFAULT_EMPTY_FORMAT;
                }
            } else {
                this.remindDateTimeNotValid = true;
                this.remindDateTimeData = DEFAULT_EMPTY_FORMAT;
            }

            // assign saved value for related
            this.related.setValue(this.quickTask.entities);

            // assign saved value for assignee
            allUsersAndTeams.forEach(user => {
                this.quickTask.assignee.forEach(item => {
                    if (user.id === item.id) {
                        this.assignee.value.push(user);
                    }
                });
            });

            if (this.quickTask.finishFlag === 1) {
                this.finishFlag.setValue(true);
                if (this.quickTask.finishNumber === 0) {
                    this.finishNumber.setValue('All');
                } else {
                    this.finishNumber.setValue(this.quickTask.finishNumber);
                }
            } else {
                this.finishFlag.setValue(false);
                this.finishNumber.setValue('All');
            }
        } else {
            this.quickTask = new QuickTask();
            // assign value for title
            this.quickTaskTitle = NEW_QUICK_TASK_TITLE;
            // assign default value for assignee
            if (this.currentUser) {
                const user = allUsersAndTeams.filter(item => item.id === this.currentUser.id);
                if (user && user.length > 0) {
                    this.assignee.value.push(user[0]);
                }
            }
            this.finishNumber.setValue('All');
        }
    }

    private _initRelatedDataItems(entityList) {
        this.relatedDataItems = this._entityService.mapEntityList(entityList).entities;
    }

    private _markAsDoneAction(subTaskId: string) {
        if (this._isCreateQuickTaskFormDataChanged) {
            if (this._doValidation()) {
                this._setControlsValues();
                this._store.dispatch({
                    type: WorkflowTaskActionTypes.EDIT_QUICK_TASK,
                    payload: {
                        quickTask: this.quickTask,
                        triggerBy: 'Done'
                    }
                });
            }
        } else {
            this._store.dispatch({
                type: WorkflowTaskActionTypes.CHANGE_QUICK_TASK_STATUS,
                payload: {
                    subTaskId: subTaskId,
                    type: 'TaskStatusChange'
                }
            });
        }
    }

    private _searchingAssignee(filterStr: string) {
        this.assigneelist.loading = true;
        this._destroySubscriptions.push(
            this._store.pipe(
                select(usersAndTeamsSelector),
                filter((list: any) => list.length > 0),
            ).subscribe(list => {
                this.assigneelist.loading = false;
                this.assigneelist.toggle(true);
                this.assigneeDataItems = list.filter(item => {
                    if (item.name.toLowerCase().indexOf(filterStr.toLowerCase()) > -1) { return item; }
                    if (item.shortName.toLowerCase().indexOf(filterStr.toLowerCase()) > -1) { return item; }
                    if (item.aliases.filter(value => value.toLowerCase().indexOf(filterStr.toLowerCase()) > -1).length > 0) { return item; }
                });
            }));
    }

    private _searchingRelated(filterStr: string) {
        this.entitylist.loading = true;
        this._destroySubscriptions.push(this._entityService.getEntityListBySearchTextQuick(1, 50, filterStr)
            .subscribe(
                res => {
                    this.entitylist.loading = false;
                    this.entitylist.toggle(true);
                    this._initRelatedDataItems(res);
                }
            ));
    }

    private _setControlsStatus() {
        if (this.operationType === OperationType.Create) {
            this.showMarkAsDoneBtn = false;
            this.finishFlag.setValue(false);
            this.finishFlag.disable();
        } else if (this.operationType === OperationType.Edit) {
            if (this.quickTask && this.quickTask.assignee && this.quickTask.subTask.find(item => item.assignee.id === this.currentUser.id)) {
                // first, the assignee should include the current user
                if (this.currentUser.id === this.quickTask.originator.id) {
                    // if the current user id = originator, can edit all control
                    this.controlReadonly = false;
                } else if (this.currentUser.id !== this.quickTask.originator.id) {
                    // if the current user id != originator, cannot edit the control, only reminder
                    this.controlReadonly = true;
                }
                // for reminder date, we can edit
                this.remindDateTimeNotValid = false;
                this.reminderReadonly = false;
                this.showMarkAsDoneBtn = true;
            } else {
                // can edit all the control, but cannot edit the reminder date
                this.controlReadonly = false;
                this.remindDateTimeNotValid = true;
                this.reminderReadonly = true;
                this.showMarkAsDoneBtn = false;
            }

            // below logic is control the MarkAsDone Btn
            if (this.quickTask && (this.quickTask.subTask && this.quickTask.subTask.length > 0) && this.showMarkAsDoneBtn) {
                const subTask = this.quickTask.subTask.find(item => item.assignee.id === this.currentUser.id);
                if (subTask && subTask.status === 'done') {
                    this.showMarkAsDoneBtn = false;
                }
            }

            this.finishFlag.disable();
            this.finishNumber.disable();
        }

        if (this.subTask) {
            if (this.subTask.status === QuickTaskStatus.DONE) {
                this.hideFooter = true;
                this.controlReadonly = true;
                this.reminderReadonly = true;
                this.remindDateTimeNotValid = true;
            }
        }
    }

    private _setControlsValues() {
        // assign values to the quick task model
        this.quickTask.subject = this.subject.value;
        this.quickTask.detail = this.details.value;
        this.quickTask.remindDate = this._getReminderDate();

        if (this.dueDate.value) {
            const dueDate = this._dateHelperService.getDateStringByDateObject(this.dueDate.value, 'yyyy-mm-dd');
            this.quickTask.dueDate = dueDate;
        }
        this.quickTask.priority = this._getPriorityValue(this.priority.value);
        const assigneeArray = [];
        this.assignee.value.forEach(item => {
            assigneeArray.push(item.id);
        });
        this.quickTask.assignee = assigneeArray;
        const relatedArray = [];
        this.related.value.forEach(item => {
            relatedArray.push(item.id);
        });
        this.quickTask.entities = relatedArray;
        this.quickTask.finishFlag = this.finishFlag.value === true ? 1 : 0;
        if (this.quickTask.finishFlag === 1) {
            if (this.finishNumber.value === 'All') {
                this.quickTask.finishNumber = 0;
            } else {
                this.quickTask.finishNumber = Number(this.finishNumber.value);
            }
        } else {
            this.quickTask.finishNumber = 0;
        }
    }

    private _setFinishStats(userIds) {
        if (this.operationType === OperationType.Create) {
            // set value for finish number
            this.finishNumberSource = [];
            this.finishNumberSource = this.finishNumberSource.concat(this._getArrayByUserCount(userIds.length));

            // set the finish flag
            if (userIds.length > 1) {
                this.finishFlag.enable();
            } else if (userIds.length === 1) {
                this.finishFlag.setValue(false);
                this.finishFlag.disable();
            }

            // set the finish number
            if (this.finishFlag.value) {
                this.finishNumber.enable();
            } else {
                this.finishNumber.disable();
            }
        } else {
            if (userIds.length < this.finishNumber.value) {
                this.finishNumber.setValue('All');
                this.quickTask.finishNumber = 0;
            }
        }
    }

    private _setFinishStatusForUserIdsAndTeamIds(teamIds, userIds) {
        if (teamIds) {
            const observableArray: Array<Observable<any>> = [];

            teamIds.forEach(id => {
                observableArray.push(this._userService.getUsersByTeamId(id).pipe(
                    take(1),
                ));
            });

            forkJoin(observableArray).subscribe(res => {
                res.forEach(item => {
                    item.data.forEach(user => {
                        if (!userIds.includes(user.data.id)) {
                            userIds.push(user.data.id);
                        }
                    });
                });
                this._setFinishStats(userIds);
            });
        }
    }
}
